import { z } from 'zod';
import { AGENT_TOOLS, type AgentTool } from './enums.js';

// Prompt layers (PRD §8, §8.0). Composition order, most general first:
//   application constraints (code, not editable) → global instructions (platform) → developer defaults
//   (workspace) → project facts/overrides → channel style → verified contact/conversation state.

export const GlobalPromptContentSchema = z.object({
  base: z.string().max(20_000),
  voice: z.string().max(10_000),
  whatsapp: z.string().max(10_000),
  toolUse: z.string().max(10_000),
  /** «Identidad del agente»: how the agent presents itself and what it answers when asked whether it is a person. */
  identityVoice: z.string().max(2_000).default(''),
  identityWhatsapp: z.string().max(2_000).default(''),
  /** Live Call Copilot guidance (addendum §2). Separate field: never part of the autonomous voice/WhatsApp prompts. */
  copilot: z.string().max(10_000).default(''),
});
export type GlobalPromptContent = z.infer<typeof GlobalPromptContentSchema>;

export const ToolInstructionSchema = z.object({
  description: z.string().max(2_000),
  whenToCall: z.string().max(2_000),
  arguments: z.string().max(2_000),
  successWording: z.string().max(2_000),
  failureWording: z.string().max(2_000),
});
export type ToolInstruction = z.infer<typeof ToolInstructionSchema>;

/** Strict: only tools of the registry may appear; an unknown tool (e.g. a reservation tool) is rejected. */
export const ToolInstructionContentSchema = z.strictObject(
  Object.fromEntries(AGENT_TOOLS.map((t) => [t, ToolInstructionSchema])) as Record<AgentTool, typeof ToolInstructionSchema>,
);
export type ToolInstructionContent = z.infer<typeof ToolInstructionContentSchema>;

export const QUALIFICATION_FIELDS = [
  'budget',
  'preferred_unit_type',
  'purpose',
  'timeline',
  'preferred_area',
  'payment_preference',
  'visit_availability',
] as const;
export const QualificationFieldSchema = z.enum(QUALIFICATION_FIELDS);
export type QualificationField = z.infer<typeof QualificationFieldSchema>;

export const QUALIFICATION_FIELD_LABELS: Record<QualificationField, string> = {
  budget: 'Presupuesto',
  preferred_unit_type: 'Tipo de unidad',
  purpose: 'Propósito (vivir / invertir)',
  timeline: 'Plazo de compra',
  preferred_area: 'Zona preferida',
  payment_preference: 'Forma de pago',
  visit_availability: 'Disponibilidad para visita',
};

/** Workspace/project editable layer. Plain-language fields first; main prompt for advanced users. */
export const AgentPromptContentSchema = z.object({
  agentName: z.string().max(80).default(''),
  persona: z.string().max(4_000).default(''),
  /** Overrides the global identity wording (both channels) for this workspace or project. Empty = use the global one. */
  identity: z.string().max(2_000).default(''),
  developerContext: z.string().max(10_000).default(''),
  projectFacts: z.string().max(10_000).default(''),
  qualificationFields: z.array(QualificationFieldSchema).default([]),
  escalationRules: z.string().max(4_000).default(''),
  whatsappStyle: z.string().max(2_000).default(''),
  voiceStyle: z.string().max(2_000).default(''),
  mainPrompt: z.string().max(20_000).default(''),
});
export type AgentPromptContent = z.infer<typeof AgentPromptContentSchema>;

export const PromptLayerSchema = z.object({
  key: z.enum(['application', 'global', 'channel_global', 'identity', 'tools', 'workspace', 'project', 'channel_style', 'contact_state']),
  title: z.string(),
  editable: z.boolean(),
  version: z.string().nullable(),
  text: z.string(),
});
export type PromptLayer = z.infer<typeof PromptLayerSchema>;

export const PromptConflictSchema = z.object({ layer: z.string(), message: z.string() });

export const CompiledPromptSchema = z.object({
  channel: z.enum(['whatsapp', 'voice']),
  globalVersion: z.number().int().nullable(),
  toolVersion: z.number().int().nullable(),
  workspacePromptVersion: z.number().int().nullable(),
  projectPromptVersion: z.number().int().nullable(),
  layers: z.array(PromptLayerSchema),
  tools: z.array(z.object({ name: z.string(), description: z.string() })),
  conflicts: z.array(PromptConflictSchema),
  compiledText: z.string(),
  approxTokens: z.number().int(),
  cacheKey: z.string(),
});
export type CompiledPrompt = z.infer<typeof CompiledPromptSchema>;

// PRD §8: "Do not fabricate a human identity". Identity wording is editable, so these heuristics WARN (in the editors
// and in the compiled prompt) when a sentence claims to be a person or hides that the agent is virtual. They never
// block a save: a person reviews the warning.
const AI_WORDS = /\b(asistente virtual|inteligencia artificial|ia|robot|bot|chatbot|automatizad[oa]s?|virtual|máquina)\b/i;
const HUMAN_WORDS = /\b(persona|humano|humana|ser humano|asesor|asesora|ejecutiv[oa]|vendedor|vendedora|agente de ventas|gerente|representante)\b/i;

// C6 (setter spec §02, §05.21): by day LIA does not announce spontaneously that it is virtual (approved commercial
// decision) and at night the SYSTEM adds the explicit AI introduction. So a day-only rule — "don't announce it unless
// asked / spontaneously" — is compatible; denying, hiding or lying when asked never is.
const DAY_ONLY = /\b(salvo|a menos|excepto|solo si|sólo si)\b[^.;]{0,50}\bpregunt\w*|\bespont[aá]ne\w*|\bpor iniciativa propia\b|\bde entrada\b/i;
const HARD_HIDE = /\b(nieg\w*|mientas|mentir|ocult\w*|admitas|reconozcas|confieses)\b|\baunque (te )?pregunten\b/i;

export interface IdentityCheckOptions {
  /** The system adds the night AI introduction (WhatsApp, 21:00–8:00). Default true. */
  nightIdentityBySystem?: boolean;
}

const IDENTITY_RULES: { test: (s: string, o: Required<IdentityCheckOptions>) => boolean; message: string }[] = [
  {
    // "Eres Sofía, asesora de ventas", "Soy una persona", "Preséntate como Andy, ejecutiva…" — a human role without
    // saying it is virtual. "No afirmes ser una persona" (negated) is the honest rule itself.
    test: (s) => /\b(eres|soy|preséntate como|presentate como|di que eres|hazte pasar por|finge ser|actúa como)\b/i.test(s) && HUMAN_WORDS.test(s) && !AI_WORDS.test(s) && !/\b(no|nunca|jamás)\b/i.test(s),
    message: 'Presenta al agente como una persona. El agente no puede fabricar una identidad humana: aclara que es un asistente virtual.',
  },
  {
    test: (s) => /\bfinge ser\b|\bhazte pasar por\b/i.test(s) && !/\b(no|nunca|jamás)\b/i.test(s),
    message: 'Pide fingir otra identidad. El agente no puede fabricar una identidad humana.',
  },
  {
    // "No te presentes como asistente virtual", "Nunca digas que eres una IA", "niega ser un bot".
    test: (s, o) =>
      ((/\b(no|nunca|jamás)\b.{0,40}\b(digas|menciones|reveles|admitas|reconozcas|aclares|te presentes|anuncies|confieses)\b/i.test(s) && AI_WORDS.test(s)) ||
        (/\bnieg\w*\b/i.test(s) && AI_WORDS.test(s))) &&
      !(o.nightIdentityBySystem && DAY_ONLY.test(s) && !HARD_HIDE.test(s)),
    message: 'Pide ocultar o negar que el agente es un asistente virtual. Si preguntan, debe responder con la verdad.',
  },
];

function identitySentences(text: string): string[] {
  return text
    .split(/(?<=[.!?\n])\s+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/** Warnings for identity wording that claims to be a person or hides being virtual. Empty = nothing to flag. */
export function detectIdentityConflicts(text: string, opts: IdentityCheckOptions = {}): string[] {
  const o: Required<IdentityCheckOptions> = { nightIdentityBySystem: opts.nightIdentityBySystem ?? true };
  const out: string[] = [];
  for (const sentence of identitySentences(text)) {
    for (const rule of IDENTITY_RULES) {
      if (!rule.test(sentence, o)) continue;
      const msg = `${rule.message} («${sentence.length > 120 ? `${sentence.slice(0, 117)}…` : sentence}»)`;
      if (!out.some((m) => m.startsWith(rule.message))) out.push(msg);
    }
  }
  return out;
}
